import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { PictureComponent } from '../picture/picture.component';
import { Picture } from '../picture/picture.model';
import { HelpersService } from '../../services/helpers.service';
import { GlobalModalService } from '../../services/global-modal.service';
import { ContainerComponent } from '../container/container.component';

export interface ResourceHeroPosition {
  width: number;
  top: number;
  right: number;
  height?: number;
}

export interface ResourceHeroDisclaimerPosition {
  left: number;
  bottom: number;
}

export interface ResourceHeroSlide {
  id: string;
  backgroundImageName: string;
  backgroundUsesTabletVariant?: boolean;
  sillhouetteImageName: string;
  sillhouetteUsesTabletVariant?: boolean;
  sillhouette: {
    mobile?: ResourceHeroPosition;
    tablet?: ResourceHeroPosition;
    desktop?: ResourceHeroPosition;
  };
  disclaimer?: {
    mobile?: ResourceHeroDisclaimerPosition;
    tablet?: ResourceHeroDisclaimerPosition;
    desktop?: ResourceHeroDisclaimerPosition;
  };
  sillhouetteAlt?: string;
  title?: string;
  text: string;
  cta?: {
    url: string;
    text: string;
  };
  isFirstSlide?: boolean;
}

@Component({
  selector: '[resource-hero]',
  imports: [CommonModule, PictureComponent, ContainerComponent],
  templateUrl: './resource-hero.component.html',
  styleUrl: './resource-hero.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'position-relative',
  },
})
export class ResourceHeroComponent {
  private helpersService = inject(HelpersService);
  private modalService = inject(GlobalModalService);
  private router = inject(Router);
  private changeDetectorRef = inject(ChangeDetectorRef);

  @Input() slides: ResourceHeroSlide[] = [];

  mobileLayout = 390;
  tabletBreakpoint = 768;
  desktopBreakpoint = 1024;
  desktopMaxBreakpoint = 1440;

  get isMobileLayout(): boolean {
    return this.helpersService.isMobile(this.mobileLayout);
  }

  get isMobile(): boolean {
    return this.helpersService.isMobile(this.tabletBreakpoint);
  }

  get isTablet(): boolean {
    return this.helpersService.isMobile(this.desktopBreakpoint);
  }

  get isBelowDesktopMax(): boolean {
    return this.helpersService.isMobile(this.desktopMaxBreakpoint);
  }

  getBackgroundImage(slide: ResourceHeroSlide): string {
    const suffix = this.isMobile
      ? '_s'
      : this.isTablet && slide.backgroundUsesTabletVariant !== false
      ? '_m'
      : '';
    const extension = this.helpersService.canUseWebp() ? 'webp' : 'png';

    return `url(/assets/img/global-hero-images/${slide.backgroundImageName}${suffix}@2x.${extension})`;
  }

  getSillhouetteData(slide: ResourceHeroSlide): Picture {
    const suffix =
      !this.isMobile &&
      this.isTablet &&
      slide.sillhouetteUsesTabletVariant !== false
        ? '_m'
        : '';

    return {
      image: `global-hero-images/${slide.sillhouetteImageName}${suffix}`,
      format: 'png',
      altext: slide.sillhouetteAlt || '',
      mobile: slide.sillhouette.mobile && this.isMobile,
      webp: true,
      fill: false,
      width: `${slide.sillhouette.desktop?.width || 0}`,
      height: `${slide.sillhouette.desktop?.height || 595}`,
      classes: 'img-fluid w-100',
    };
  }

  getSillhouetteStyles(slide: ResourceHeroSlide) {
    const position = this.isMobile
      ? slide.sillhouette.mobile
      : this.isTablet
      ? slide.sillhouette.tablet
      : slide.sillhouette.desktop;

    return {
      width: this.toResponsiveLength(position?.width || 0),
      top: this.toResponsiveLength(position?.top || 0),
      right: this.toResponsiveLength(position?.right || 0),
    };
  }

  getDisclaimerStyles(slide: ResourceHeroSlide) {
    const position = this.isMobile
      ? slide.disclaimer?.mobile
      : this.isTablet
      ? slide.disclaimer?.tablet
      : slide.disclaimer?.desktop;

    return {
      left: this.toResponsiveLength(position?.left || 0),
      bottom: this.toResponsiveLength(position?.bottom || 0),
    };
  }

  onClick(event: Event, url: string): void {
    event.preventDefault();

    if (url.startsWith('http')) {
      this.modalService.openModal('interstitial', url);
      return;
    }

    this.router.navigateByUrl(url);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.changeDetectorRef.markForCheck();
  }

  private toResponsiveLength(value: number): string {
    return this.isMobileLayout
      ? this.helpersService.toREMMobile(value)
      : this.isMobile
      ? this.helpersService.toVW(value, this.mobileLayout)
      : this.isTablet
      ? this.helpersService.toVW(value, this.tabletBreakpoint)
      : this.isBelowDesktopMax
      ? this.helpersService.toVW(value, this.desktopMaxBreakpoint)
      : this.helpersService.toREM(value);
  }
}
